<template>
  <li :class="itemClass">
    <component
      :is="componentType"
      v-bind="componentAttrs"
      class="nav-link fw-bold d-flex align-items-center rounded-start-3 rounded-end-0 py-2 w-100"
      :class="dynamicClasses"
      @mouseover="isHovered = true"
      @mouseout="isHovered = false"
    >
      <font-awesome-icon v-if="icon" :icon="icon" class="me-2 flex-shrink-0" />
      <span class="flex-grow-1">
        <slot>{{ text }}</slot>
        <label
          v-if="adminId"
          class="alert alert-subtle-warning text-center admin-extra-info py-0 px-1 ms-1 d-inline-block"
        >
          {{ adminId }}
        </label>
      </span>
      <span v-if="hasAction && !isActive" class="sidenav-action-dot" />
    </component>
    <div v-if="showActions && isActive" class="sidenav-action-container">
      <slot name="actions"></slot>
    </div>
  </li>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import type { RouteLocationRaw } from "vue-router";

const props = defineProps<{
  to?: RouteLocationRaw;
  text?: string;
  icon?: string;
  isActive?: boolean;
  hasAction?: boolean;
  showActions?: boolean;
  itemClass?:
    string | Record<string, boolean> | (string | Record<string, boolean>)[];
  adminId?: string | number | null;
  disableHover?: boolean;
}>();

const isHovered = ref(false);

const componentType = computed(() => (props.to ? "RouterLink" : "div"));

const componentAttrs = computed(() => (props.to ? { to: props.to } : {}));

const dynamicClasses = computed(() => {
  const currentlyActive = !!props.to && !!props.isActive;

  if (currentlyActive) {
    return ["bg-body-highlight", "text-secondary", "active", "border-0"];
  } else if (isHovered.value && !props.disableHover) {
    return [
      "bg-body-secondary",
      "text-secondary",
      "border",
      "border-end-big",
      "border-gray-400",
    ];
  } else {
    return [
      "bg-gray-1000",
      "text-gray-200",
      "border",
      "border-end-big",
      "border-gray-1000",
    ];
  }
});
</script>

<style scoped lang="scss">
.nav-link {
  transition:
    background-color 0.15s ease-in-out,
    border-color 0.15s ease-in-out,
    color 0.15s ease-in-out;
  position: relative;

  &.active {
    --curve-radius: 7px;
    --curve-color: var(--phoenix-body-highlight-bg);

    &::before,
    &::after {
      content: "";
      position: absolute;
      right: 0;
      width: var(--curve-radius);
      height: var(--curve-radius);
      background-color: var(--curve-color);
      mask-repeat: no-repeat;
      -webkit-mask-repeat: no-repeat;
    }

    &::before {
      top: calc(-1 * var(--curve-radius));
      mask-image: radial-gradient(
        circle var(--curve-radius) at top left,
        transparent 0,
        transparent var(--curve-radius),
        black calc(var(--curve-radius) + 0.5px)
      );
      -webkit-mask-image: radial-gradient(
        circle var(--curve-radius) at top left,
        transparent 0,
        transparent var(--curve-radius),
        black calc(var(--curve-radius) + 0.5px)
      );
    }

    &::after {
      bottom: calc(-1 * var(--curve-radius));
      mask-image: radial-gradient(
        circle var(--curve-radius) at bottom left,
        transparent 0,
        transparent var(--curve-radius),
        black calc(var(--curve-radius) + 0.5px)
      );
      -webkit-mask-image: radial-gradient(
        circle var(--curve-radius) at bottom left,
        transparent 0,
        transparent var(--curve-radius),
        black calc(var(--curve-radius) + 0.5px)
      );
    }
  }
}

.sidenav-action-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: var(--phoenix-orange-50);
  flex-shrink: 0;
  margin-left: 0.5rem;
}

.sidenav-action-container {
  position: relative;
  padding-left: 1.75rem;
  padding-top: 0.375rem;
  padding-bottom: 0.5rem;

  &::before {
    content: "";
    position: absolute;
    left: 1rem;
    top: 0;
    width: 0.75rem;
    height: calc(50% + 0.25rem);
    border-left: 1.5px solid var(--phoenix-orange-50);
    border-bottom: 1.5px solid var(--phoenix-orange-50);
    border-bottom-left-radius: 4px;
    opacity: 0.55;
  }
}

.admin-extra-info {
  font-size: 0.75em;
  vertical-align: middle;
}

.border-end-big {
  border-right-width: 5px !important;
}
</style>
